import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store'

const API_URL = import.meta.env.VITE_API_URL || '/api/v1'

// Instancia de Axios configurada
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para agregar token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo mostrar toast para errores 401 (sesion expirada)
    // Los errores 403 y 500 son manejados por las paginas con datos demo
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
      toast.error('Sesion expirada')
    }
    // No mostrar toast para 403 ni 500 - las paginas usan datos demo
    return Promise.reject(error)
  }
)

// ========== AUTH ==========
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
}

// ========== CIRUGÍAS ==========
export const cirugiasAPI = {
  getAll: (params) => api.get('/cirugias', { params }),
  getById: (id) => api.get(`/cirugias/${id}`),
  create: (data) => api.post('/cirugias', data),
  updateEstado: (id, data) => api.put(`/cirugias/${id}/estado`, data),
  getEstadisticas: (params) => api.get('/cirugias/estadisticas/resumen', { params }),
}

// ========== PACIENTES ==========
export const pacientesAPI = {
  getAll: (params) => api.get('/pacientes', { params }),
  getById: (id) => api.get(`/pacientes/${id}`),
  create: (data) => api.post('/pacientes', data),
  update: (id, data) => api.put(`/pacientes/${id}`, data),
  getUbicacion: (id) => api.get(`/pacientes/${id}/ubicacion`),
  getGeolocalizacion: (params) => api.get('/pacientes/geolocalizacion/todos', { params }),
}

// ========== HOSPITALES ==========
export const hospitalesAPI = {
  getAll: (params) => api.get('/hospitales', { params }),
  getById: (id) => api.get(`/hospitales/${id}`),
  getSaturacion: (id) => api.get(`/hospitales/${id}/saturacion`),
  getMapa: () => api.get('/hospitales/mapa/todos'),
  getComparativa: (id) => api.get(`/hospitales/${id}/comparativa`),
}

// ========== DASHBOARD ==========
export const dashboardAPI = {
  getKPIs: (params) => api.get('/dashboard/kpis', { params }),
  getDistribucionTiempos: (params) => api.get('/dashboard/distribucion-tiempos', { params }),
  getTendenciaMensual: (params) => api.get('/dashboard/tendencia-mensual', { params }),
  getPorEspecialidad: (params) => api.get('/dashboard/por-especialidad', { params }),
  getHospitalesComparativa: (params) => api.get('/dashboard/hospitales-comparativa', { params }),
  getMotivosSuspension: (params) => api.get('/dashboard/motivos-suspension', { params }),
  getEstrategia230100: (params) => api.get('/dashboard/estrategia-2-30-100', { params }),
}

// ========== ALERTAS ==========
export const alertasAPI = {
  getAll: (params) => api.get('/alertas', { params }),
  getNoLeidasCount: (params) => api.get('/alertas/no-leidas/count', { params }),
  marcarLeida: (id) => api.put(`/alertas/${id}/leer`),
  marcarTodasLeidas: (data) => api.put('/alertas/leer-todas', data),
  create: (data) => api.post('/alertas', data),
  delete: (id) => api.delete(`/alertas/${id}`),
}

// ========== UPLOADS ==========
export const uploadAPI = {
  preview: (formData) => api.post('/upload/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  import: (data) => api.post('/upload/import', data),
  getHistory: (params) => api.get('/upload/history', { params }),
  downloadTemplate: (tipo) => api.get(`/upload/template/${tipo}`, { responseType: 'blob' }),
}

// ========== REPORTES ==========
export const reportesAPI = {
  getCirugias: (params) => api.get('/reportes/cirugias', { params }),
  getRezagadas: (params) => api.get('/reportes/rezagadas', { params }),
  getHospitales: (params) => api.get('/reportes/hospitales', { params }),
  getProductividad: (params) => api.get('/reportes/productividad', { params }),
  getSuspensiones: (params) => api.get('/reportes/suspensiones', { params }),
  
  // Descargar como Excel
  downloadExcel: async (endpoint, params, filename) => {
    const response = await api.get(endpoint, {
      params: { ...params, formato: 'excel' },
      responseType: 'blob'
    })
    
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${filename}_${Date.now()}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

export default api
